// src/pages/AdminStatsView.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';
import {
  Calendar, DollarSign, ShoppingBag, Package, Users, TrendingUp, AlertCircle,
  Download, CheckCircle, XCircle, ShoppingCart, RefreshCw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/AdminStatsView.css';
import { API_BASES } from '../config';

const STATS_API_BASE_URL = API_BASES.stats;

const AdminStatsView = () => {
  const { authToken, isAuthLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCollaborator, setSelectedCollaborator] = useState('');

  const [completeStats, setCompleteStats] = useState(null);
  const [collaboratorSales, setCollaboratorSales] = useState([]);
  const [productSales, setProductSales] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastUpdated, setLastUpdated] = useState(null);

  const COLORS = ['#FF00FF', '#00C49F', '#FFBB28', '#0088FE', '#FF8042', '#8884d8'];

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const monthOptions = [
    { value: '', label: 'Todo el año' },
    { value: '1', label: 'Enero' },
    { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' },
    { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  const apiCall = useCallback(async (path, options = {}) => {
    if (!authToken) {
      throw new Error("No hay token de autenticación disponible. Por favor, inicie sesión.");
    }
    const fullUrl = `${STATS_API_BASE_URL}${path}`;
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    if (!response.ok) {
      let errorMessage = `Error ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData?.message) errorMessage = errorData.message;
        else if (errorData) errorMessage = JSON.stringify(errorData);
      } catch (e) {
        const text = await response.text();
        console.error("Error al parsear error:", text);
        if (response.status === 401) errorMessage = "Acceso no autorizado. Por favor, inicie sesión como administrador.";
        else if (response.status === 403) errorMessage = "Permiso denegado. No tiene los roles necesarios.";
      }
      throw new Error(errorMessage);
    }
    return response.json();
  }, [authToken]);

  // --- Loaders (parametrizados) ---
  const loadCompleteStats = useCallback(async (year, month) => {
    if (!authToken) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ year: String(year), ...(month ? { month } : {}) });
      const data = await apiCall(`/admin/complete?${params}`);
      setCompleteStats(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar estadísticas generales: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, authToken]);

  const loadCollaboratorSales = useCallback(async (year, month) => {
    if (!authToken) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({ year: String(year), ...(month ? { month } : {}) });
      const data = await apiCall(`/admin/collaborators?${params}`);
      setCollaboratorSales(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar ventas por creador: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, authToken]);

  const loadProductSales = useCallback(async (year, month, collaborator) => {
    if (!authToken) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        year: String(year),
        ...(month ? { month } : {}),
        ...(collaborator ? { collaborator } : {})
      });
      const data = await apiCall(`/admin/products?${params}`);
      setProductSales(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Error al cargar ventas por producto: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [apiCall, authToken]);

  // 1ª carga únicamente del tab activo
  useEffect(() => {
    if (isAuthLoading) return;
    if (!authToken) {
      setError("Token de autenticación no encontrado. Por favor, inicie sesión.");
      return;
    }
    handleRefresh();
  }, [authToken, isAuthLoading, activeTab, selectedYear, selectedMonth, selectedCollaborator]);

  const handleRefresh = () => {
    if (activeTab === 'overview') {
      loadCompleteStats(selectedYear, selectedMonth);
    } else if (activeTab === 'collaborators') {
      loadCollaboratorSales(selectedYear, selectedMonth);
    } else if (activeTab === 'products') {
      loadCollaboratorSales(selectedYear, selectedMonth);
      loadProductSales(selectedYear, selectedMonth, selectedCollaborator);
    }
  };

  useEffect(() => {
    setSelectedCollaborator('');
  }, [selectedYear, selectedMonth]);


  const formatCurrency = (amount) =>
    new Intl.NumberFormat('es-EC', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 })
      .format(amount || 0);

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return alert('No hay datos para exportar.');
    const header = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).join(','));
    const csvContent = "data:text/csv;charset=utf-8," + [header, ...rows].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${filename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
    <div className={`stat-card-item card-border-${color}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="d-flex align-items-center">
        <div className={`icon-circle icon-bg-${color}`}>
          <Icon className={`w-6 h-6 icon-text-${color}`} />
        </div>
        <div className="ms-3">
          <h3 className="card-title fs-6 fw-medium text-secondary mb-1">{title}</h3>
          <p className="card-text fs-3 fw-bold text-dark mb-0">{value}</p>
          {subtitle && <p className="card-text text-sm text-secondary mb-0">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const OverviewTab = () => (
    <div className="d-grid gap-4">
      <div className="stats-card-minimal p-4 mb-4">
        <h3 className="card-title fs-5 fw-bold mb-3 d-flex align-items-center text-dark">
          <TrendingUp className="w-6 h-6 me-2 text-fuchsia-custom" />
          Panel de Resumen Estratégico
        </h3>
        <div className="row g-3">
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Users className="w-6 h-6 me-2 text-primary" />
              <div>
                <div className="fs-5 fw-bold text-dark">{completeStats?.totalCollaborators?.toLocaleString() || '0'}</div>
                <div className="text-secondary">Creadores Totales</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <Package className="w-6 h-6 me-2 text-success" />
              <div>
                <div className="fs-5 fw-bold text-dark">{completeStats?.totalProductsCount?.toLocaleString() || '0'}</div>
                <div className="text-secondary">Productos Totales</div>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="d-flex align-items-center">
              <TrendingUp className="w-6 h-6 me-2 text-info" />
              <div>
                <div className="fs-5 fw-bold text-dark">
                  {completeStats?.monthlyGrowthPercentage ? `${completeStats.monthlyGrowthPercentage.toFixed(2)}%` : 'N/A'}
                </div>
                <div className="text-secondary">Crecimiento Último Mes</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-4">
        <div className="col">
          <StatCard title="Ingresos Brutos" value={formatCurrency(completeStats?.totalRevenue)} subtitle="Ingresos generados antes de comisión" icon={DollarSign} color="fuchsia" />
        </div>
        <div className="col">
          <StatCard title="Comisión AECBlock" value={formatCurrency((completeStats?.totalRevenue || 0) * 0.5)} subtitle="50% de los ingresos brutos" icon={DollarSign} color="blue" />
        </div>
        <div className="col">
          <StatCard title="Órdenes Completadas" value={completeStats?.totalOrders?.toLocaleString()} icon={ShoppingBag} color="purple" onClick={() => setActiveTab('products')} />
        </div>
        <div className="col">
          <StatCard title="Clientes Adquiridos" value={completeStats?.totalCustomers?.toLocaleString() || '0'} icon={Users} color="orange" onClick={() => alert('Funcionalidad de desglose de clientes no implementada.')} />
        </div>
      </div>

      {completeStats?.monthlySales?.length > 0 ? (
        <div className="stats-card-minimal">
          <h3 className="card-title fs-5 fw-bold mb-4 text-dark">Tendencia de Ventas Mensuales</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={completeStats.monthlySales}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis tickFormatter={(value) => `$${value}`} />
              <Tooltip formatter={(value) => [formatCurrency(value), 'Venta Mensual']} />
              <Legend />
              <Line type="monotone" dataKey="revenue" name="Venta Mensual" stroke="#FF00FF" strokeWidth={3} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="stats-card-minimal text-center p-5">
          <TrendingUp className="mx-auto mb-4 text-secondary" style={{ width: '4rem', height: '4rem' }} />
          <h3 className="mt-2 fs-5 fw-bold text-dark">Sin datos de ventas mensuales</h3>
          <p className="mt-1 fs-6 text-secondary">No hay datos de ventas mensuales para el período seleccionado.</p>
        </div>
      )}

      {completeStats?.topProductsLast30Days?.length > 0 ? (
        <div className="stats-card-minimal">
          <h3 className="card-title fs-5 fw-bold mb-4 text-dark">Top Productos Vendidos (Últimos 30 Días)</h3>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={completeStats.topProductsLast30Days} margin={{ top: 16, right: 16, bottom: 100, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="productName" angle={-45} textAnchor="end" height={100} dy={10} interval={0} tick={{ fontSize: 12 }} tickFormatter={(v) => (v?.length > 18 ? v.slice(0, 18) + '…' : v)} />
              <YAxis />
              <Tooltip labelFormatter={(label) => `Producto: ${label}`} formatter={(value) => [value, 'Cantidad Vendida']} />
              <Legend verticalAlign="top" align="right" height={36} wrapperStyle={{ paddingBottom: 8 }} />
              <Bar dataKey="totalQuantity" name="Cantidad Vendida" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="stats-card-minimal text-center p-5">
          <ShoppingCart className="mx-auto mb-4 text-secondary" style={{ width: '4rem', height: '4rem' }} />
          <h3 className="mt-2 fs-5 fw-bold text-dark">Sin datos de top productos</h3>
          <p className="mt-1 fs-6 text-secondary">No se encontraron datos de los productos más vendidos para el período.</p>
        </div>
      )}
    </div>
  );

  const CollaboratorsTab = () => (
    <div className="stats-card-minimal">
      <h3 className="card-title fs-5 fw-bold mb-4 text-dark">Ventas por Creador</h3>
      <div className="mb-4 d-flex gap-2">
        <button className="btn btn-outline-info btn-sm" disabled={!collaboratorSales?.length} onClick={() => exportToCSV(collaboratorSales, 'ventas_colaboradores')}>
          <Download className="me-1" size={16} /> Exportar CSV
        </button>
      </div>
      <div className="mb-4">
        {collaboratorSales?.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={collaboratorSales.slice(0, 10)}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="collaboratorUsername" angle={-45} textAnchor="end" height={80} />
              <YAxis tickFormatter={(value) => `$${value}`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="totalSales" fill="#FF00FF" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-center p-5 text-secondary">No hay datos de ventas por creador para el período seleccionado.</div>
        )}
      </div>
      <div className="table-responsive">
        {collaboratorSales?.length > 0 ? (
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Creador</th>
                <th>País</th>
                <th>Órdenes</th>
                <th>Cantidad Vendida</th>
                <th>Ventas Totales</th>
              </tr>
            </thead>
            <tbody>
              {collaboratorSales.map((c, i) => (
                <tr key={i}>
                  <td className="fs-6 fw-medium text-dark">{c.collaboratorUsername}</td>
                  <td className="fs-6 text-secondary">{c.country}</td>
                  <td className="fs-6 text-secondary">{c.ordersCount?.toLocaleString()}</td>
                  <td className="fs-6 text-secondary">{c.totalQuantity?.toLocaleString()}</td>
                  <td className="fs-6 text-dark">{formatCurrency(c.totalSales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center p-5 text-secondary">No hay creadores para mostrar.</div>
        )}
      </div>
    </div>
  );

  const ProductsTab = () => (
    <div className="stats-card-minimal">
      <h3 className="card-title fs-5 fw-bold mb-4 text-dark">Ventas por Producto</h3>
      <div className="mb-4 d-flex gap-2 align-items-center">
        <label htmlFor="selectCollaborator" className="form-label fs-6 fw-semibold text-secondary mb-0">Filtrar por Creador:</label>
        <select
          id="selectCollaborator"
          value={selectedCollaborator}
          onChange={(e) => setSelectedCollaborator(e.target.value)}
          className="form-select form-select-custom w-auto d-inline-block"
        >
          <option value="">Todos los creadores</option>
          {collaboratorSales.map((collaborator, index) => (
            <option key={index} value={collaborator.collaboratorUsername}>
              {collaborator.collaboratorUsername}
            </option>
          ))}
        </select>
        <button className="btn btn-outline-info btn-sm ms-auto" disabled={!productSales?.length} onClick={() => exportToCSV(productSales, 'ventas_productos')}>
          <Download className="me-1" size={16} /> Exportar CSV
        </button>
      </div>

      <div className="table-responsive">
        {productSales?.length > 0 ? (
          <table className="table table-striped table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Producto</th>
                <th>Creador</th>
                <th>País</th>
                <th>Precio Unit.</th>
                <th>Cantidad</th>
                <th>Ventas Totales</th>
              </tr>
            </thead>
            <tbody>
              {productSales.slice(0, 20).map((p, i) => (
                <tr key={i}>
                  <td className="fs-6 fw-medium text-dark">{p.productName}</td>
                  <td className="fs-6 text-secondary">{p.uploaderUsername}</td>
                  <td className="fs-6 text-secondary">{p.country}</td>
                  <td className="fs-6 text-secondary">{formatCurrency(p.unitPrice)}</td>
                  <td className="fs-6 text-secondary">{p.totalQuantity?.toLocaleString()}</td>
                  <td className="fs-6 text-dark">{formatCurrency(p.totalSales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center p-5 text-secondary">No hay productos para mostrar.</div>
        )}
      </div>
    </div>
  );

  return (
    <div className="stats-view-container">
      <div className="container">
        <div className="mb-5">
          <h1 className="admin-stats-title mb-2">
            Panel de Estadísticas - <span className="text-fuchsia-custom">Administrador</span>
          </h1>
          <p className="lead text-secondary">
            Vista general de la plataforma
            {lastUpdated && (
              <span className="ms-2 small text-muted">
                (Últ. actualización: {lastUpdated.toLocaleString()})
              </span>
            )}
          </p>
        </div>

        {/* Filtros + Botón Actualizar */}
        <div className="stats-card-minimal mb-4">
          <div className="row g-4 align-items-end">
            <div className="col-md-4">
              <label htmlFor="selectYear" className="form-label fs-6 fw-semibold text-secondary mb-2">
                <Calendar className="d-inline-block me-2 text-fuchsia-custom" style={{ width: '1.25rem', height: '1.25rem' }} />
                Año
              </label>
              <select
                id="selectYear"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="form-select form-select-custom"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4">
              <label htmlFor="selectMonth" className="form-label fs-6 fw-semibold text-secondary mb-2">
                Mes (Opcional)
              </label>
              <select
                id="selectMonth"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="form-select form-select-custom"
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>

            <div className="col-md-4 d-flex align-items-end justify-content-end gap-2">
              <button
                className="btn btn-outline-primary d-inline-flex align-items-center"
                onClick={handleRefresh}
                disabled={loading || !authToken}
                title="Actualizar estadísticas"
              >
                {loading ? (
                  <span className="d-inline-flex align-items-center">
                    <span className="spinner-border spinner-border-sm me-2" role="status" />
                    Actualizando…
                  </span>
                ) : (
                  <>
                    <RefreshCw size={16} className="me-2" />
                    Actualizar
                  </>
                )}
              </button>
              {loading && (
                <div className="d-flex align-items-center text-fuchsia-600-custom fs-6 fw-semibold">
                  <div className="spinner-border spinner-border-sm ms-2 spinner-border-fuchsia" role="status">
                    <span className="visually-hidden">Cargando...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center rounded-3 shadow-sm p-4 mb-4" role="alert">
            <AlertCircle className="me-3" style={{ width: '1.5rem', height: '1.5rem' }} />
            <div className="flex-grow-1">
              <h3 className="alert-heading fs-5 fw-semibold mb-1">Error</h3>
              <p className="mb-0 fs-6">{error}</p>
            </div>
            <button type="button" className="btn-close-error btn-close-white ms-auto" aria-label="Cerrar" onClick={() => setError(null)}>×</button>
          </div>
        )}

        <div className="stats-card-minimal mb-4">
          <ul className="nav nav-tabs nav-tabs-minimal card-header-tabs">
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                <TrendingUp className="d-inline-block me-2" style={{ width: '1.25rem', height: '1.25rem' }} />
                Resumen General
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'collaborators' ? 'active' : ''}`} onClick={() => setActiveTab('collaborators')}>
                <Users className="d-inline-block me-2" style={{ width: '1.25rem', height: '1.25rem' }} />
                Creadores
              </button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
                <Package className="d-inline-block me-2" style={{ width: '1.25rem', height: '1.25rem' }} />
                Productos
                {completeStats?.productsPendingReview > 0 && (
                  <span className="badge bg-danger ms-2 rounded-pill">{completeStats.productsPendingReview}</span>
                )}
              </button>
            </li>
          </ul>
        </div>

        {activeTab === 'overview' && completeStats && <OverviewTab />}
        {activeTab === 'collaborators' && <CollaboratorsTab />}
        {activeTab === 'products' && <ProductsTab />}
      </div>
    </div>
  );
};

export default AdminStatsView;
